import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import {
  canCancel,
  FIELDS_PER_RESOURCE,
  FLAT_PRODUCTION_PER_HOUR,
  RESOURCE_KINDS,
  buildingStats,
  fieldProduction,
  storageRow,
  type Resources,
  type WireTraining,
  type WireUnit,
} from "@empire/shared";
import { UNITS } from "@empire/core-sim";
import {
  formatDuration,
  levelsOf,
  predictedCapacity,
  predictedProduction,
  predictedResources,
  remainingMs,
  useVillageStore,
} from "../../store/village.js";
import { useSessionStore } from "../../store/session.js";
import { useAttackStore } from "../../store/attack.js";
import { useUiStore } from "../../store/ui.js";
import { ResourceIcon } from "../components/ResourceIcon.js";
import { BuildingPicture } from "../components/BuildingPicture.js";
import { VillageView, type VillageState } from "../../pixi/VillageView.js";
import { GOALS, nextGain, type Goal } from "../../village/guide.js";
import { useCurrentGoal } from "../../store/goals.js";
import { claimChest } from "../../api/progress.js";
import { useToastStore } from "../../store/toast.js";

/**
 * The village (S8-4, redrawn Sep 2026): the home screen.
 *
 * It used to be a list of seventeen rows. It is now the village itself — the
 * buildings on their plots, alive, with a level on each — and you work it the
 * way the genre has taught everyone to: tap a building to see it and upgrade
 * it, tap a resource to see where it comes from. The next-step banner at the
 * top still says what to do, and now points at the building to tap.
 *
 * Every number on screen is still predicted each frame from the server's
 * baseline with the server's own functions — not animated, not guessed.
 */

const RESOURCE_LABELS: Record<string, string> = {
  wood: "Wood",
  clay: "Clay",
  iron: "Iron",
  grain: "Grain",
};

/** Which field makes which resource. */
const FIELD_FOR: Record<string, string> = {
  wood: "woodcutter",
  clay: "clay_pit",
  iron: "iron_mine",
  grain: "grain_farm",
};

export function Village({ onOpenDefence }: { onOpenDefence: () => void }): JSX.Element {
  const status = useVillageStore((s) => s.status);
  const economy = useVillageStore((s) => s.economy);
  const error = useVillageStore((s) => s.error);
  const sessionStatus = useSessionStore((s) => s.status);
  const sessionVillage = useSessionStore((s) => s.villageId);
  const sessionError = useSessionStore((s) => s.error);
  const username = useSessionStore((s) => s.username);
  const [selected, setSelected] = useState<string | null>(null);
  const [resource, setResource] = useState<string | null>(null);
  const goal = useCurrentGoal();

  // First load once there is a session, then a slow poll: everything visible
  // is predicted locally between answers.
  useEffect(() => {
    if (sessionStatus !== "ready" || !sessionVillage) return;
    void useVillageStore.getState().load();
    // The next-step banner needs to know which barbarian villages you have beaten.
    void useAttackStore.getState().loadTargets();
    const poll = setInterval(() => void useVillageStore.getState().load({ quiet: true }), 15_000);
    return () => clearInterval(poll);
  }, [sessionStatus, sessionVillage]);

  // One animation frame drives every number on the screen.
  useEffect(() => {
    let frame = 0;
    const step = (): void => {
      useVillageStore.getState().tick();
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, []);

  if (!economy) {
    if (status === "loading") return <Centre>Reading your village…</Centre>;
    // No village and no session is a different problem from a village that
    // would not load, and the player can usually act on it — the server may be
    // asleep, without a database, or refusing new accounts for an hour.
    // The server refused this device's saved sign-in. "Try again" re-ran the
    // same refused sign-in forever — a dead end straight after the tutorial.
    // Only a new village gets past it, and that is the player's call.
    if (sessionStatus === "lost") {
      return (
        <Centre>
          This device is no longer signed in to its village.
          <button
            type="button"
            data-testid="start-new-village"
            onClick={() => {
              void useSessionStore.getState().startOver();
            }}
            className="mt-3 h-11 rounded-lg bg-gold px-4 text-xs font-bold text-bg"
          >
            Start a new village
          </button>
          <button
            type="button"
            data-testid="lost-log-in"
            onClick={() => useUiStore.getState().openAccount("login")}
            className="mt-2 h-11 rounded-lg border border-panel-edge bg-panel px-4 text-xs font-semibold text-ink"
          >
            I saved my village — log in
          </button>
        </Centre>
      );
    }
    if (!sessionVillage) {
      return (
        <Centre>
          {sessionError ??
            (sessionStatus === "connecting" ? "Signing you in…" : "Not signed in yet.")}
          <button
            type="button"
            onClick={() => {
              void useSessionStore.getState().connect();
            }}
            className="mt-3 h-11 rounded-lg bg-gold px-4 text-xs font-bold text-bg"
          >
            Try again
          </button>
        </Centre>
      );
    }
    return (
      <Centre>
        {error ?? "Your village did not load."}
        <button
          type="button"
          onClick={() => void useVillageStore.getState().load()}
          className="mt-3 h-11 rounded-lg bg-gold px-4 text-xs font-bold text-bg"
        >
          Try again
        </button>
      </Centre>
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      <ResourceBar onPick={setResource} />
      {/* Above the village, not over it: the village is framed to fit the
          space that is left, and a card on top hid its back row. */}
      <NextStepBanner goal={goal.goal} index={goal.index} onOpenDefence={onOpenDefence} />
      <ComeBackStrip />
      <div className="relative min-h-0 flex-1">
        <VillageCanvas onTap={setSelected} hint={hintFor(goal.goal)} selected={selected} />
        {username === null ? (
          // Until saved, this village exists only as a key in this browser
          // (D-116). Always one tap away, never in the way.
          <button
            type="button"
            data-testid="save-village"
            onClick={() => useUiStore.getState().openAccount("save")}
            className="absolute left-3 top-3 h-10 rounded-full border border-gold/60 bg-bg-raised/90 px-3 text-[11px] font-bold text-gold shadow-lg"
          >
            Save your village
          </button>
        ) : null}
        <button
          type="button"
          data-testid="open-defence"
          onClick={onOpenDefence}
          className="absolute bottom-3 right-3 h-12 rounded-full border border-gold/60 bg-bg-raised/90 px-4 text-xs font-bold text-gold shadow-lg"
        >
          Defence
        </button>
      </div>
      {selected ? (
        <BuildingSheet
          id={selected}
          onClose={() => setSelected(null)}
          onOpenDefence={onOpenDefence}
        />
      ) : null}
      {resource ? (
        <ResourceSheet
          kind={resource}
          onClose={() => setResource(null)}
          onOpenBuilding={(id) => {
            setResource(null);
            setSelected(id);
          }}
        />
      ) : null}
    </div>
  );
}

/** The building the next step wants tapped. */
function hintFor(goal: Goal | null): string | null {
  if (!goal) return null;
  const action = goal.action;
  switch (action.kind) {
    case "build":
      return action.building;
    case "train":
      return unitHome(action.unit) ?? "barracks";
    case "attack":
      return "rally_point";
    case "defence":
      return "wall";
  }
}

// ===========================================================================
// The scene
// ===========================================================================

/**
 * The Pixi village, fed from the store about once a second.
 *
 * Levels, the build queue, what can be built, training and the hint are all
 * the view needs; it decides for itself what to redraw.
 */
function VillageCanvas({
  onTap,
  hint,
  selected,
}: {
  onTap: (id: string) => void;
  hint: string | null;
  selected: string | null;
}): JSX.Element {
  const [labels, setLabels] = useState(() => {
    try {
      return localStorage.getItem("empire.village.labels") === "true";
    } catch {
      return false;
    }
  });
  const presentation = useRef({ selected, labels });
  presentation.current = { selected, labels };
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<VillageView | null>(null);
  const tap = useRef(onTap);
  tap.current = onTap;
  const economy = useVillageStore((s) => s.economy);
  const second = useVillageStore((s) => Math.floor(s.predictedAtMs / 1000));
  const hintRef = useRef(hint);
  hintRef.current = hint;

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let cancelled = false;
    let mounted: VillageView | undefined;
    void VillageView.mount(el).then((v) => {
      if (cancelled) {
        v.destroy();
        return;
      }
      mounted = v;
      view.current = v;
      if (import.meta.env.DEV) (window as unknown as { __village?: VillageView }).__village = v;
      v.onTap((id) => tap.current(id));
      v.setPresentation(presentation.current.selected, presentation.current.labels);
      v.setState(
        stateOf(
          useVillageStore.getState().economy,
          useVillageStore.getState().predictedAtMs,
          hintRef.current
        )
      );
    });
    return () => {
      cancelled = true;
      view.current = null;
      mounted?.destroy();
    };
  }, []);

  useEffect(() => {
    view.current?.setState(stateOf(economy, second * 1000, hint));
  }, [economy, second, hint]);

  useEffect(() => {
    view.current?.setPresentation(selected, labels);
  }, [selected, labels]);

  return (
    <>
      <div
        ref={host}
        className="absolute inset-0 touch-none [&>canvas]:block"
        data-testid="village-canvas"
        role="img"
        aria-label="Your village. Tap a building to view it, drag to explore, or use the camera controls. Turn on Labels to show every building name."
      />
      <div
        className="absolute right-3 top-3 flex flex-col gap-2"
        role="group"
        aria-label="Village camera"
      >
        <button
          className="empire-control"
          type="button"
          aria-label="Zoom in"
          onClick={() => view.current?.zoomBy(1.35)}
        >
          ＋
        </button>
        <button
          className="empire-control"
          type="button"
          aria-label="Zoom out"
          onClick={() => view.current?.zoomBy(1 / 1.35)}
        >
          －
        </button>
        <button
          className="empire-control text-[11px]"
          type="button"
          aria-label="Fit village"
          onClick={() => view.current?.frame()}
        >
          Fit
        </button>
      </div>
      <button
        className="empire-control absolute bottom-3 left-3 px-3 text-[11px]"
        type="button"
        aria-pressed={labels}
        onClick={() => {
          const next = !labels;
          setLabels(next);
          try {
            localStorage.setItem("empire.village.labels", String(next));
          } catch {
            /* Presentation works without storage. */
          }
        }}
      >
        Labels
      </button>
    </>
  );
}

function stateOf(
  economy: ReturnType<typeof useVillageStore.getState>["economy"],
  nowMs: number,
  hint: string | null
): VillageState {
  if (!economy) return { levels: {}, building: [], buildable: new Set(), training: false, hint };
  const levels = levelsOf(economy);
  return {
    levels,
    building: economy.queue.map((item) => {
      const total = Math.max(1, item.finishAtMs - item.startedAtMs);
      const left = remainingMs(item, nowMs);
      return { id: item.building, progress: 1 - left / total, label: clock(left) };
    }),
    buildable: new Set(
      economy.buildings
        .filter((b) => b.level === 0 && b.missingPrerequisites.length === 0)
        .map((b) => b.id)
    ),
    training: economy.training.length > 0,
    hint,
  };
}

/** "2:41", "1:05:12" — a countdown, for a bubble. */
function clock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const two = (n: number): string => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
}

function Centre({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 px-6 text-center text-sm text-ink-dim">
      {children}
    </div>
  );
}

/**
 * The four counters, each a button: tap one to see where it comes from.
 *
 * `useShallow` is load-bearing: these selectors build a fresh object every
 * time, and without comparing the four numbers React would loop until it
 * unmounted the tree (see git history for the blank-screen bug it caused).
 */
function ResourceBar({ onPick }: { onPick: (kind: string) => void }): JSX.Element {
  const resources = useVillageStore(useShallow(predictedResources));
  const capacity = useVillageStore(useShallow(predictedCapacity));
  const production = useVillageStore(useShallow(predictedProduction));

  return (
    <div className="empire-resources empire-parchment grid shrink-0 grid-cols-4 gap-px border-b border-panel-edge bg-panel-edge">
      {RESOURCE_KINDS.map((kind) => {
        const full = resources[kind] >= capacity[kind];
        const starving = production[kind] < 0;
        return (
          <button
            type="button"
            key={kind}
            onClick={() => onPick(kind)}
            className="bg-bg-raised px-2 py-2 text-left active:bg-panel"
            data-testid={`res-${kind}`}
          >
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-ink-dim">
              <ResourceIcon kind={kind} size={22} />
              {RESOURCE_LABELS[kind]}
            </div>
            <div
              className={`text-sm font-semibold tabular-nums ${
                full ? "text-warn" : starving ? "text-danger" : "text-ink"
              }`}
            >
              {resources[kind].toLocaleString()}
            </div>
            <div className="text-[10px] tabular-nums text-ink-dim">
              {full ? "full" : `${production[kind] >= 0 ? "+" : ""}${production[kind]}/h`}
            </div>
            <div className="resource-capacity" aria-hidden="true">
              <span
                style={{
                  width: `${Math.max(0, Math.min(100, (resources[kind] / Math.max(1, capacity[kind])) * 100))}%`,
                }}
              />
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ===========================================================================
// Sheets
// ===========================================================================

/** A panel that slides up from the bottom, over the village. */
function Sheet({
  onClose,
  children,
  testid,
}: {
  onClose: () => void;
  children: React.ReactNode;
  testid: string;
}): JSX.Element {
  return (
    <div
      className="absolute inset-0 z-30 flex flex-col justify-end bg-black/45"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className="empire-parchment empire-sheet max-h-[78%] overflow-y-auto rounded-t-2xl border-t border-panel-edge bg-bg-raised px-4 pb-4 pt-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-panel-edge" />
        {children}
        <button
          type="button"
          onClick={onClose}
          className="mt-3 h-11 w-full rounded-lg border border-panel-edge text-xs font-semibold text-ink-dim"
        >
          Close
        </button>
      </div>
    </div>
  );
}

/**
 * Everything about one building: what it is, what the next level gives, the
 * price, and the button — plus what that building is FOR (train at the
 * Barracks, see the army at the Rally Point, edit the defence at the Wall).
 */
function BuildingSheet({
  id,
  onClose,
  onOpenDefence,
}: {
  id: string;
  onClose: () => void;
  onOpenDefence: () => void;
}): JSX.Element | null {
  const economy = useVillageStore((s) => s.economy);
  const predictedAtMs = useVillageStore((s) => s.predictedAtMs);
  const busy = useVillageStore((s) => s.busy);
  const error = useVillageStore((s) => s.error);
  const errorBuilding = useVillageStore((s) => s.errorBuilding);
  const build = useVillageStore((s) => s.build);
  const cancel = useVillageStore((s) => s.cancel);
  const setTab = useUiStore((s) => s.setTab);
  if (!economy) return null;
  const building = economy.buildings.find((b) => b.id === id);
  if (!building) return null;
  const stats = buildingStats(id);
  const next = building.next;
  const queued = economy.queue.find((q) => q.building === id);
  const blocked = building.missingPrerequisites.length > 0;
  const slotsFull = economy.queue.length >= economy.buildSlots;
  const units = economy.units.filter((u) => unitHome(u.id) === id);
  const name = building.name.replace(" (field)", "");

  return (
    <Sheet onClose={onClose} testid="building-sheet">
      <div className="flex gap-3">
        <BuildingPicture id={building.level > 0 ? id : "plot"} level={building.level} size={92} />
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-ink" data-testid="sheet-title">
            {name}
            {building.level > 0 ? (
              <span className="font-normal text-ink-dim"> · level {building.level}</span>
            ) : null}
          </p>
          <p className="text-[11px] text-ink-dim">{stats.function}</p>
          {next ? (
            <p className="mt-1.5 text-sm font-semibold text-ok">
              {building.level === 0 ? "Build it: " : `Level ${next.level}: `}
              {nextGain(id, building.level)}
            </p>
          ) : (
            <p className="mt-1.5 text-sm text-ok">At its maximum level.</p>
          )}
        </div>
      </div>

      {next && !blocked && !queued ? (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs tabular-nums text-ink-dim">
          <Price cost={next.cost} shortfall={next.shortfall} />
          <span>· {formatDuration(next.durationMs ?? next.timeSeconds * 1000)}</span>
        </div>
      ) : null}

      {queued ? (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-gold/40 bg-panel px-3 py-2">
          <p className="flex-1 text-xs text-gold">
            Building level {queued.level} — {formatDuration(remainingMs(queued, predictedAtMs))}{" "}
            left
          </p>
          <button
            type="button"
            onClick={() => void cancel(queued.id)}
            disabled={busy === queued.id || !canCancel(queued.startedAtMs, predictedAtMs)}
            title="Full refund during the first minute; then work is committed."
            className="h-10 rounded-lg bg-panel-edge px-3 text-[11px] font-semibold text-ink-dim"
          >
            Cancel
          </button>
        </div>
      ) : next && blocked ? (
        <p className="mt-3 rounded-lg bg-panel px-3 py-2.5 text-xs text-ink-dim">
          Needs{" "}
          {building.missingPrerequisites
            .map(
              (need) => `${buildingStats(need.building).name} ${need.level} (you have ${need.have})`
            )
            .join(" and ")}
          .
        </p>
      ) : next ? (
        <>
          <button
            type="button"
            data-testid={`build-${id}`}
            disabled={busy === id || !next.affordable || slotsFull}
            onClick={() => void build(id)}
            className="mt-3 h-12 w-full rounded-lg bg-gold text-sm font-bold text-bg disabled:bg-panel-edge disabled:text-ink-dim"
          >
            {busy === id
              ? "…"
              : building.level === 0
                ? `Build ${name}`
                : `Upgrade to level ${next.level}`}
          </button>
          {slotsFull ? (
            <p className="mt-1.5 text-center text-[11px] text-ink-dim">
              Your builder is busy — free in{" "}
              {formatDuration(Math.min(...economy.queue.map((q) => remainingMs(q, predictedAtMs))))}
              .
            </p>
          ) : !next.affordable ? (
            <p className="mt-1.5 text-center text-[11px] text-ink-dim">
              {next.readyInMs === null
                ? "This village cannot save up for it yet — build more storage."
                : `Enough resources in ${formatDuration(next.readyInMs)}.`}
            </p>
          ) : null}
        </>
      ) : null}

      {error && errorBuilding === id ? <p className="mt-2 text-[11px] text-warn">{error}</p> : null}

      {/* What the building is FOR. */}
      {units.length > 0 ? (
        <section className="mt-4">
          <h3 className="text-[10px] font-semibold uppercase tracking-wide text-ink-dim">
            Train here
          </h3>
          <Training />
          <div className="mt-2 space-y-2">
            {units.map((unit) => (
              <UnitRow key={unit.id} unit={unit} />
            ))}
          </div>
        </section>
      ) : null}
      {id === "rally_point" ? (
        <ArmySummary
          onAttack={() => {
            onClose();
            setTab("map");
          }}
        />
      ) : null}
      {id === "wall" || id === "engineers_guild" ? (
        <button
          type="button"
          onClick={() => {
            onClose();
            onOpenDefence();
          }}
          className="mt-4 h-11 w-full rounded-lg border border-gold/60 text-xs font-bold text-gold"
        >
          Edit your defence
        </button>
      ) : null}
      {id === "warehouse" || id === "granary" ? (
        <StorageNote id={id} level={building.level} />
      ) : null}
    </Sheet>
  );
}

/** The building a unit is trained in. */
function unitHome(unitId: string): string | null {
  return UNITS.find((u) => u.id === unitId)?.trainedIn?.building ?? null;
}

function ArmySummary({ onAttack }: { onAttack: () => void }): JSX.Element | null {
  const economy = useVillageStore((s) => s.economy);
  if (!economy) return null;
  const army = economy.units.filter((u) => u.have > 0);
  return (
    <section className="mt-4">
      <h3 className="text-[10px] font-semibold uppercase tracking-wide text-ink-dim">
        Your army · {economy.grainUpkeepPerHour} grain/h
      </h3>
      <p className="mt-1 text-xs text-ink">
        {army.length === 0
          ? "Nobody at home. Train Militia at the Barracks."
          : army.map((u) => `${u.have} ${u.name}`).join(" · ")}
      </p>
      <button
        type="button"
        onClick={onAttack}
        className="mt-3 h-11 w-full rounded-lg bg-gold text-xs font-bold text-bg"
      >
        Plan an attack
      </button>
    </section>
  );
}

function StorageNote({ id, level }: { id: string; level: number }): JSX.Element {
  const row = storageRow(Math.max(1, level));
  return (
    <p className="mt-4 rounded-lg bg-panel px-3 py-2.5 text-xs text-ink-dim">
      Holds {row.capacity.toLocaleString()}{" "}
      {id === "granary" ? "grain" : "of each of wood, clay and iron"}. When it is full, production
      stops. {row.protected.toLocaleString()} of it can never be raided.
    </p>
  );
}

/**
 * Where a resource comes from, where it goes, and the upgrade that helps.
 * The answer to "how do I get more wood?", where players look for it.
 */
function ResourceSheet({
  kind,
  onClose,
  onOpenBuilding,
}: {
  kind: string;
  onClose: () => void;
  onOpenBuilding: (id: string) => void;
}): JSX.Element | null {
  const economy = useVillageStore((s) => s.economy);
  const resources = useVillageStore(useShallow(predictedResources));
  const capacity = useVillageStore(useShallow(predictedCapacity));
  const production = useVillageStore(useShallow(predictedProduction));
  if (!economy) return null;
  const k = kind as keyof Resources;
  const levels = levelsOf(economy);
  const fieldId = FIELD_FOR[kind]!;
  const fieldLevel = levels[fieldId] ?? 0;
  const fromFields = FIELDS_PER_RESOURCE * fieldProduction(fieldLevel);
  const upkeep = kind === "grain" ? economy.grainUpkeepPerHour : 0;
  const net = production[k];
  const room = capacity[k] - resources[k];
  const store = kind === "grain" ? "granary" : "warehouse";
  const field = economy.buildings.find((b) => b.id === fieldId);
  const gain = field?.next ? nextGain(fieldId, fieldLevel) : null;

  return (
    <Sheet onClose={onClose} testid="resource-sheet">
      <div className="flex items-center gap-2">
        <ResourceIcon kind={kind} size={22} />
        <p className="text-base font-semibold text-ink">
          {RESOURCE_LABELS[kind]} · {resources[k].toLocaleString()} / {capacity[k].toLocaleString()}
        </p>
      </div>

      <dl className="mt-3 space-y-1.5 text-xs tabular-nums">
        <Row label="Every village" value={`+${FLAT_PRODUCTION_PER_HOUR}/h`} />
        <Row
          label={`${buildingStats(fieldId).name.replace(" (field)", "")} level ${fieldLevel} (3 fields)`}
          value={`+${fromFields}/h`}
        />
        {upkeep > 0 ? <Row label="Your army eats" value={`−${upkeep}/h`} danger /> : null}
        <div className="border-t border-panel-edge pt-1.5">
          <Row label="Per hour" value={`${net >= 0 ? "+" : ""}${net}/h`} strong />
        </div>
      </dl>

      <p className="mt-3 text-[11px] text-ink-dim">
        {net <= 0
          ? kind === "grain"
            ? "Your army eats more grain than the farms make. Upgrade the Grain Farm, or keep a smaller army."
            : "Nothing is being added."
          : room <= 0
            ? `Your ${store === "granary" ? "Granary" : "Warehouse"} is full — production has stopped. Build it up.`
            : `Full in ${formatDuration((room / net) * 3600_000)}. Then production stops until you spend or build more storage.`}
      </p>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onOpenBuilding(fieldId)}
          className="rounded-lg bg-gold px-3 py-2.5 text-left text-bg"
        >
          <span className="block text-xs font-bold">
            More {RESOURCE_LABELS[kind]?.toLowerCase()}
          </span>
          <span className="block text-[10px] opacity-80">
            {gain
              ? `${buildingStats(fieldId).name.replace(" (field)", "")}: ${gain}`
              : "At its maximum"}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onOpenBuilding(store)}
          className="rounded-lg border border-panel-edge bg-panel px-3 py-2.5 text-left text-ink"
        >
          <span className="block text-xs font-bold">Store more</span>
          <span className="block text-[10px] text-ink-dim">
            {store === "granary" ? "Granary" : "Warehouse"}
          </span>
        </button>
      </div>
    </Sheet>
  );
}

function Row({
  label,
  value,
  danger,
  strong,
}: {
  label: string;
  value: string;
  danger?: boolean;
  strong?: boolean;
}): JSX.Element {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-ink-dim">{label}</dt>
      <dd className={danger ? "text-danger" : strong ? "font-semibold text-ink" : "text-ink"}>
        {value}
      </dd>
    </div>
  );
}

/** The four numbers, with the ones you are short of marked. */
function Price({
  cost,
  shortfall,
}: {
  cost: Resources;
  shortfall: Partial<Resources>;
}): JSX.Element {
  return (
    <span className="flex flex-wrap gap-x-2">
      {RESOURCE_KINDS.map((kind) => (
        <span
          key={kind}
          className={`inline-flex items-center gap-0.5 ${shortfall[kind] ? "text-danger" : ""}`}
        >
          <ResourceIcon kind={kind} size={11} />
          {cost[kind].toLocaleString()}
        </span>
      ))}
    </span>
  );
}

function Training(): JSX.Element | null {
  const economy = useVillageStore((s) => s.economy);
  const predictedAtMs = useVillageStore((s) => s.predictedAtMs);
  const busy = useVillageStore((s) => s.busy);
  const cancel = useVillageStore((s) => s.cancelTraining);
  if (!economy || economy.training.length === 0) return null;

  return (
    <div className="mt-2 space-y-2" data-testid="training">
      {economy.training.map((order: WireTraining) => (
        <div
          key={order.id}
          className="flex items-center gap-3 rounded-lg border border-gold/40 bg-panel px-3 py-2"
        >
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-ink">
              {order.name} · {order.delivered} of {order.count}
            </div>
            <div className="text-[11px] tabular-nums text-gold">
              next in {formatDuration(Math.max(0, order.nextAtMs - predictedAtMs))} · all done in{" "}
              {formatDuration(Math.max(0, order.finishAtMs - predictedAtMs))}
            </div>
          </div>
          <button
            type="button"
            data-testid={`cancel-training-${order.unit}`}
            disabled={busy === order.id || !canCancel(order.startedAtMs, predictedAtMs)}
            title="Full refund until one minute after this order starts."
            onClick={() => void cancel(order.id)}
            className="h-11 rounded-lg bg-panel-edge px-3 text-[11px] font-semibold text-ink-dim hover:text-ink disabled:opacity-40"
          >
            Cancel
          </button>
        </div>
      ))}
    </div>
  );
}

const BATCHES = [1, 5, 10];

function UnitRow({ unit }: { unit: WireUnit }): JSX.Element {
  const busy = useVillageStore((s) => s.busy);
  const error = useVillageStore((s) => s.error);
  const errorBuilding = useVillageStore((s) => s.errorBuilding);
  const train = useVillageStore((s) => s.train);

  return (
    <div
      className="rounded-lg border border-panel-edge bg-panel px-3 py-2"
      data-testid={`unit-${unit.id}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-ink">
          {unit.name}
          {unit.have > 0 ? <span className="text-ink-dim"> · {unit.have}</span> : null}
        </span>
        <span className="text-[10px] text-ink-dim">{unit.role}</span>
      </div>

      {unit.needs ? (
        <p className="mt-1 text-[11px] text-ink-dim">
          Needs {unit.needs.name} {unit.needs.level}.
        </p>
      ) : (
        <>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 text-[11px] tabular-nums text-ink-dim">
            <Price cost={unit.cost} shortfall={{}} />
            <span>· {formatDuration(unit.trainSeconds * 1000)} each</span>
            <span>· {unit.upkeepGrainPerHour} grain/h each</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            {BATCHES.map((count) => (
              <button
                key={count}
                type="button"
                data-testid={`train-${unit.id}-${count}`}
                disabled={busy === unit.id || unit.affordable < count}
                onClick={() => void train(unit.id, count)}
                className={`h-11 rounded-lg px-3 text-[11px] font-bold ${
                  unit.affordable >= count ? "bg-gold text-bg" : "bg-panel-edge text-ink-dim"
                } disabled:opacity-50`}
              >
                +{count}
              </button>
            ))}
            <span className="text-[11px] text-ink-dim">
              {unit.trainingBlock ??
                (unit.affordable > 0 ? `can train ${unit.affordable}` : "cannot afford one")}
            </span>
          </div>
        </>
      )}

      {error && errorBuilding === unit.id ? (
        <p className="mt-1 text-[11px] text-warn" data-testid={`error-${unit.id}`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ===========================================================================
// The next step
// ===========================================================================

/**
 * The guide, as a banner over the village: the step, why, and a button that
 * does it — or says why it can't yet. The arrow in the scene points at the
 * building the step is about.
 */
function NextStepBanner({
  goal,
  index,
  onOpenDefence,
}: {
  goal: Goal | null;
  index: number;
  onOpenDefence: () => void;
}): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  if (!goal) return null;
  return (
    <div
      className="empire-guide empire-parchment mx-2 mt-1 shrink-0 rounded-xl border border-gold/50 px-3 shadow-lg"
      data-testid="next-step"
    >
      <div className="flex items-center gap-2 py-1">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-controls="village-guide-details"
          className="min-h-11 min-w-0 flex-1 text-left"
        >
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-gold">
            Next step · {index + 1}/{GOALS.length} · {expanded ? "Less ▴" : "Details ▾"}
          </span>
          <span className="block text-xs font-semibold text-ink" data-testid="next-step-title">
            {goal.title}{" "}
            <span className="text-[10px] font-semibold text-gold" data-testid="next-step-reward">
              +{goal.reward} each
            </span>
          </span>
        </button>
        <GoalButton goal={goal} onOpenDefence={onOpenDefence} />
      </div>
      <p
        id="village-guide-details"
        hidden={!expanded}
        className="border-t border-panel-edge pb-2 pt-2 text-xs leading-relaxed text-ink-dim"
      >
        {goal.why}
      </p>
    </div>
  );
}

function GoalButton({
  goal,
  onOpenDefence,
}: {
  goal: Goal;
  onOpenDefence: () => void;
}): JSX.Element {
  const economy = useVillageStore((s) => s.economy)!;
  const predictedAtMs = useVillageStore((s) => s.predictedAtMs);
  const busy = useVillageStore((s) => s.busy);
  const build = useVillageStore((s) => s.build);
  const train = useVillageStore((s) => s.train);
  const targets = useAttackStore((s) => s.targets);
  const action = goal.action;

  const primary =
    "min-h-11 max-w-[46%] shrink-0 rounded-lg bg-gold px-3 py-1 text-[11px] font-bold text-bg disabled:opacity-40";
  // While the step has to wait, the Trials are the thing to do (29 Sep 2026).
  const waiting = (text: string): JSX.Element => (
    <div className="flex max-w-[48%] shrink-0 flex-col items-end gap-1">
      <p className="text-right text-[11px] leading-tight text-ink-dim">{text}</p>
      <button
        type="button"
        data-testid="next-step-trials"
        onClick={() => useUiStore.getState().setTab("trials")}
        className="min-h-11 rounded-lg border border-gold/60 px-2.5 text-[11px] font-bold text-gold"
      >
        Play a Trial meanwhile
      </button>
    </div>
  );

  if (action.kind === "defence") {
    return (
      <button type="button" className={primary} onClick={onOpenDefence} data-testid="next-step-go">
        Open your defence
      </button>
    );
  }

  if (action.kind === "attack") {
    const target = targets.find((t) => t.difficulty === action.difficulty);
    if (!target) return waiting("Looking for it on the map…");
    return (
      <button
        type="button"
        className={primary}
        data-testid="next-step-go"
        onClick={() => {
          useAttackStore.getState().confirm(target);
          useUiStore.getState().setTab("map");
        }}
      >
        Choose your army
      </button>
    );
  }

  if (action.kind === "train") {
    const unit = economy.units.find((u) => u.id === action.unit);
    if (!unit) return waiting("…");
    if (unit.needs) return waiting(`Needs ${unit.needs.name} ${unit.needs.level}.`);
    const count = Math.min(action.count, unit.affordable);
    if (count <= 0) return waiting("Not enough resources yet — they are on their way.");
    return (
      <button
        type="button"
        className={primary}
        data-testid="next-step-go"
        disabled={busy === unit.id}
        onClick={() => void train(unit.id, count)}
      >
        Train {count} {unit.name}
      </button>
    );
  }

  const building = economy.buildings.find((b) => b.id === action.building);
  if (!building || !building.next) return waiting("…");
  if (building.missingPrerequisites.length > 0) {
    return waiting(
      `Needs ${building.missingPrerequisites
        .map((need) => `${buildingStats(need.building).name} ${need.level}`)
        .join(" and ")} first.`
    );
  }
  if (economy.queue.length >= economy.buildSlots) {
    const soonest = Math.min(...economy.queue.map((item) => remainingMs(item, predictedAtMs)));
    return waiting(`Your builder is busy — free in ${formatDuration(soonest)}.`);
  }
  if (!building.next.affordable) {
    return waiting(
      building.next.readyInMs === null
        ? "This village cannot save up for it yet."
        : `Enough resources in ${formatDuration(building.next.readyInMs)}.`
    );
  }
  return (
    <button
      type="button"
      className={primary}
      data-testid="next-step-go"
      disabled={busy === building.id}
      onClick={() => void build(building.id)}
    >
      Build {building.name} level {building.next.level}
    </button>
  );
}

// ===========================================================================
// When to come back (29 Sep 2026)
// ===========================================================================

/** "21:05" — a moment on the player's own clock, `ms` from now. */
function clockIn(ms: number): string {
  return new Date(Date.now() + ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * One row under the next step: the first-day boost, the supply wagon, and when
 * the builders are done. The first session used to end with nothing on screen
 * saying when to return; now it ends with times.
 */
function ComeBackStrip(): JSX.Element | null {
  const economy = useVillageStore((s) => s.economy);
  const predictedAtMs = useVillageStore((s) => s.predictedAtMs);
  const [claiming, setClaiming] = useState(false);
  if (!economy) return null;

  const now = Math.max(predictedAtMs, economy.nowMs);

  const wagonIn = economy.chestReadyAtMs != null ? economy.chestReadyAtMs - now : null;
  const builderIn =
    economy.queue.length > 0
      ? Math.min(...economy.queue.map((q) => remainingMs(q, predictedAtMs)))
      : null;

  const claim = async (): Promise<void> => {
    const { villageId } = useSessionStore.getState();
    const token = await useSessionStore.getState().token();
    if (!villageId || !token) return;
    setClaiming(true);
    try {
      const answer = await claimChest(villageId, token);
      useVillageStore.getState().receive(answer.village);
      useToastStore.getState().show(`Supply wagon  +${answer.reward.wood} of each`);
    } catch {
      useToastStore.getState().show("The wagon is not here yet.");
    } finally {
      setClaiming(false);
    }
  };

  const chip = "shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold";
  return (
    <div
      className="empire-return-strip flex shrink-0 items-center gap-1.5 overflow-x-auto no-scrollbar px-2 py-1"
      data-testid="come-back"
    >
      <span className={`${chip} border-ok/50 text-ok`} data-testid="boost-chip">
        2 builders · levels 1–3 build 3× faster, levels 4–5 build 2× faster
      </span>
      {wagonIn !== null ? (
        wagonIn <= 0 ? (
          <button
            type="button"
            data-testid="claim-wagon"
            disabled={claiming}
            onClick={() => void claim()}
            className={`${chip} order-first border-gold bg-gold text-bg`}
          >
            🎁 Claim {economy.pendingWagons ?? 1} supply wagon(s)
          </button>
        ) : (
          <span className={`${chip} border-panel-edge text-ink-dim`} data-testid="wagon-chip">
            🎁 Next supply wagon {clockIn(wagonIn)}
          </span>
        )
      ) : null}
      {builderIn !== null ? (
        <span className={`${chip} border-panel-edge text-ink-dim`}>
          🔨 Builder free {clockIn(builderIn)}
        </span>
      ) : null}
    </div>
  );
}
